import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { translations } from "@/lib/i18n";
import { getLanguage } from "@/lib/useLanguage";

type Props = { children: ReactNode; fallback?: ReactNode };
type State = { error: Error | null };

/**
 * Top-level error boundary — catches render-phase exceptions anywhere in the
 * subtree it wraps and renders a recovery card instead of a white screen.
 *
 * Audit 260512-nmw B-5. Wired around <App /> in main.tsx so every route
 * subtree is covered. Per-route fallbacks can wrap subtrees that need a
 * more contextual recovery (e.g. /courses/:slug/play, /admin/*).
 *
 * Hand-rolled class component — no extra dep. React 19's "do nothing /
 * propagate" default on render errors leaves a blank #root, which the
 * audit flagged as the SPA's largest UX cliff.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the boundary catch in dev tooling. In prod this is the only
    // signal an operator gets until OTel ships (audit H-21).
    console.error("ErrorBoundary caught render error", error, info);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const t = translations[getLanguage()];

    return (
      <div className="min-h-dvh flex items-center justify-center bg-background p-6">
        <div
          role="alert"
          aria-live="assertive"
          className="max-w-md w-full text-center space-y-4 p-8 rounded-lg border bg-card shadow-sm"
        >
          <h1 className="text-xl font-semibold text-foreground">
            {t.error_boundary_title}
          </h1>
          <p className="text-sm text-muted-foreground">{t.error_boundary_desc}</p>
          <div className="flex gap-2 justify-center pt-2">
            <Button onClick={this.reset} variant="outline">
              {t.error_boundary_retry}
            </Button>
            <Button onClick={() => window.location.reload()}>
              {t.error_boundary_reload}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
